import React, { useEffect, useState, useRef } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { Container } from '@fiverr-private/layout_components';
import { Modal, ModalContent } from '@fiverr-private/overlay';
import { IconButton } from '@fiverr-private/button';
import { TimesIcon } from '@fiverr-private/visuals';
import { ActivationErrorType, DEFAULT_STEP_ID, StepId } from '../../constants';
import { useBreakpointMd } from '../../utils/breakpoints';
import { getEmailDomain } from '../../utils/getEmailDomain';
import { translate } from '../../utils/translate';
import { BigQueryEventProperties } from '../../events/bigQuery';
import { globalBigQueryReporter, globalMixpanelReporter } from '../../events';
import { Variant } from '../../types';
import { CodeActivationContext } from '../shared/CodeActivationContext';
import { useResendEmailWithCode } from '../CodeActivationStep/hooks/useResendEmailWithCode';
import { ActiveStep } from './ActiveStep';

interface CodeActivationModalProps {
    email: string;
    isOpen: boolean;
    variant?: Variant;
    isPro?: boolean;
    shouldSendActivationCodeOnOpen?: boolean;
    /** @deprecated */
    postActivationDelay?: number;
    onClose?: () => void;
    onError?: (error: ActivationErrorType) => void;
    onSuccess?: () => void;
}

export const CodeActivationModal = ({
    isPro = false,
    email: initialEmail,
    shouldSendActivationCodeOnOpen = false,
    postActivationDelay = 0,
    variant = 'default',
    isOpen,
    onClose,
    onError,
    onSuccess,
}: CodeActivationModalProps) => {
    const initialFocusRef = useRef<HTMLInputElement>(null);
    const [activeStepId, setActiveStepId] = useState<StepId>(DEFAULT_STEP_ID);
    const [email, setEmail] = useState(initialEmail);
    const isMdScreen = useBreakpointMd();
    const { resend } = useResendEmailWithCode({ email });

    useEffect(() => {
        if (isOpen) {
            setActiveStepId(DEFAULT_STEP_ID);
            if (shouldSendActivationCodeOnOpen) {
                resend();
            }
        }
    }, [isOpen, shouldSendActivationCodeOnOpen, resend]);

    useEffect(() => {
        const { experience } = getContext();
        const funnel: BigQueryEventProperties['funnel'] = experience.isBusiness ? 'business' : 'standard';
        globalMixpanelReporter.enrichContext({
            funnel,
            reason: 'User Trigger',
            emailDomain: getEmailDomain(email),
            codeActivationAttemptsCount: 0,
        });
        globalBigQueryReporter.enrichContext({
            funnel,
            reason: 'user_trigger',
            emailDomain: getEmailDomain(email),
            codeActivationAttemptsCount: 0,
        });
    }, [email]);

    return (
        <CodeActivationContext.Provider
            value={{
                isPro,
                stepId: activeStepId,
                goToStep: setActiveStepId,
                email,
                variant,
                postActivationDelay,
                setEmail,
                onSkip: onClose,
                onError,
                onSuccess,
                initialFocusRef,
            }}
        >
            <Modal isOpen={isOpen} onClose={onClose} fullScreenBreakpoint="sm">
                <ModalContent size={isMdScreen ? 'xl' : 'sm'} initialFocusRef={initialFocusRef}>
                    {onClose && (
                        <Container
                            position="absolute"
                            top="0px"
                            right="0px"
                            marginTop={{ default: '2', sm: '4' }}
                            marginRight={{ default: '2.5', sm: '4' }}
                        >
                            <IconButton aria-label={translate('general.close')} variant="ghost" onClick={onClose}>
                                <TimesIcon size="md" />
                            </IconButton>
                        </Container>
                    )}
                    <ActiveStep />
                </ModalContent>
            </Modal>
        </CodeActivationContext.Provider>
    );
};
